import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { FiCopy, FiLogOut } from 'react-icons/fi'
import BottomNav from '../components/BottomNav'
import { Sweep } from '../components/PageHeader'
import logo from '../assets/haio-logo.png'
import { Pass } from '../types'

type Props = {
  pass: Pass
  details: { name: string; email: string; phone: string; ticket: string }
  invite: { url: string; used: boolean } | null
}

export default function Profile({ pass, details, invite }: Props) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    if (!invite) return
    try {
      await navigator.clipboard.writeText(invite.url)
      setCopied(true)
    } catch {
      window.prompt('Copy this link', invite.url)
    }
  }

  const rows: [string, string][] = [
    ['Name', details.name],
    ['Email', details.email],
    ['Phone', details.phone],
    ['Ticket', details.ticket],
  ]

  return (
    <>
      <Head title="Profile" />
      <main className="ho-screen">
        <Sweep />
        <header className="px-6 pt-[30px]">
          <img src={logo} alt="Sahajidah Hai-O" className="block w-[90px]" />
          <p className="mt-7 text-base font-bold text-[var(--hai-o-red)]">Hey {pass.name}!</p>
          <h1 className="text-[40px] leading-[46px] font-extrabold tracking-[-1px]">My Profile</h1>
        </header>

        <div className="flex flex-col gap-6 px-6 pt-5 pb-10">
          <section aria-label="My QR code" className="ho-pop flex flex-col items-center gap-2 rounded-[20px] bg-white p-6">
            <img src={pass.qrImageUrl} alt={`QR code ${pass.qrValue}`} width={180} height={180} className="block size-[180px]" />
            <span className="text-lg font-bold tracking-widest">{pass.qrValue}</span>
          </section>

          {invite && (
            <section aria-label="Invite A Friend" className="ho-pop flex flex-col gap-3 rounded-[20px] bg-[var(--cream)] p-[18px]">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-xl font-extrabold">Invite A Friend</h2>
                <span className="ho-sticker bg-[var(--gold)]">{invite.used ? '0 invites left' : '1 invite left'}</span>
              </div>
              <p className="text-sm text-[var(--ink-soft)]">
                {invite.used
                  ? 'Your referral link has been used.'
                  : 'Share this link with one friend so they can join the event too. It works for one person only.'}
              </p>
              <p className="rounded-lg border border-[var(--line-card)] bg-white px-3 py-2 text-sm break-all">{invite.url}</p>
              {!invite.used && (
                <button type="button" className="ho-btn ho-btn-primary" onClick={copy}>
                  <FiCopy aria-hidden="true" />{copied ? 'Copied!' : 'Copy invite link'}
                </button>
              )}
            </section>
          )}

          <section aria-label="My Details">
            <h2 className="mb-3 text-xl font-extrabold">My Details</h2>
            <dl className="divide-y divide-[var(--line-soft)] rounded-2xl border border-[var(--line-card)]">
              {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 px-4 py-3 text-sm">
                  <dt className="text-[var(--ink-muted)]">{k}</dt>
                  <dd className="text-right font-semibold break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <Link href="/logout" method="delete" as="button" className="ho-btn ho-btn-outline">
            <FiLogOut aria-hidden="true" />Log out
          </Link>
        </div>

        <BottomNav current="profile" />
      </main>
    </>
  )
}
